/**
 * The dashboard rail — §AF, and §3.21's rule about where a count comes from.
 *
 * ── Ten tiles, and every one of them is a link ──────────────────────────────
 *
 * §AF's own constraint is the important half: *"do not overwhelm the user with
 * analytics that do not drive action."* A number you cannot open is a
 * statistic, and a statistic on this screen is noise. So every tile carries a
 * real `href` the server built out of the very filter object it counted, and
 * tapping one lands on `/feed` with that filter applied.
 *
 * ── The count is NEVER computed here ────────────────────────────────────────
 *
 * Acceptance #49 asserts that every tile's count equals the number of rows
 * `GET /api/feed` returns for the same filter. That holds because
 * `dashboard.ts` puts the tile's filter through `feed.ts`'s compiler, and it
 * keeps holding only as long as this screen renders `tile.value` and follows
 * `tile.href` **verbatim**. Counting the cards on the first page here — or
 * rebuilding the query string from the filter object — is exactly the bug the
 * shared compiler exists to prevent, and it would show up as a tile saying 4
 * over a list of 6.
 *
 * ── What IS rendered locally, and why ───────────────────────────────────────
 *
 * The label and the caption. The server composes them in English, because a
 * Worker has no business holding the UI's Spanish; a rail that reads
 * "NEW TODAY" under a Spanish app is the halfway retrofit CLAUDE.md rule 8
 * forbids. So the ten known keys get local copy in both locales, interpolated
 * from `tile.value` and `tile.filter` — the same numbers the server used — and
 * an unrecognised key (a tile added later) falls back to the server's own
 * `display` and `caption` rather than disappearing.
 *
 * ── A zero tile is dimmed and last. It is never hidden ──────────────────────
 *
 * "Zero follow-ups due" is the answer somebody came for. The server already
 * orders them; this screen only has to not filter them out.
 *
 * ── §AF, §10.3 ──────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useState } from 'react'
import { get } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { Link } from '../lib/route'
import { DASH, money } from '../lib/format'
import Icon, { type IconName } from '../components/Icon'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

export interface TileFilter {
  minScore?: number
  [key: string]: unknown
}

export interface Tile {
  key: string
  label: string
  kind: 'count' | 'money' | 'opportunity' | 'health'
  value: number | null
  display: string
  caption: string | null
  href: string | null
  filter: TileFilter | null
  dimmed: boolean
}

export interface DashboardView {
  businessDate: string
  tiles: Tile[]
}

/* ────────────────────────────────────────────────────────────────────────────
 * A media query, as a hook
 *
 * Exported because the feed, the board and the comparison all switch layout at
 * a breakpoint and all three are files in this unit. It is a hook rather than a
 * CSS class because `index.css` belongs to another unit in this build.
 * ────────────────────────────────────────────────────────────────────────── */

export function useMinWidth(px: number): boolean {
  const [wide, setWide] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(`(min-width: ${px}px)`).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const query = window.matchMedia(`(min-width: ${px}px)`)
    const onChange = () => setWide(query.matches)
    onChange()
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [px])

  return wide
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  heading: 'Today',
  failed: 'The dashboard could not load.',
  refresh: 'Try again',

  new_today: 'NEW TODAY',
  new_today_c: 'Found since midnight, Puerto Rico time',
  high_score: 'HIGH-SCORE LEADS',
  high_score_c: 'Scoring {n} or better',
  needs_review: 'NEEDS REVIEW',
  needs_review_c: 'Nobody has looked at these yet',
  waiting_sellers: 'WAITING FOR SELLERS',
  waiting_sellers_c: 'Contacted, no answer yet',
  followups_due: 'FOLLOW-UPS DUE',
  followups_due_c: 'Due today or already overdue',
  inspections: 'INSPECTIONS',
  inspections_c: 'At the inspection stage',
  finalists: 'FINALISTS',
  finalists_c: 'The set the comparison screen holds',
  average_landed: 'AVERAGE LANDED COST',
  average_landed_c: 'Across open leads scoring 70 or better',
  average_landed_none: 'Not enough data',
  best_current: 'BEST CURRENT OPPORTUNITY',
  best_current_c: '{n} points for every $1,000 it costs to land',
  best_current_none: 'Nothing scored yet',
  source_health: 'SOURCE HEALTH',
  source_health_ok: 'All healthy',
  source_health_bad: '{n} need attention',
  source_health_c: 'Where the leads come from',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  heading: 'Hoy',
  failed: 'No se pudo cargar el panel.',
  refresh: 'Reintentar',

  new_today: 'NUEVOS HOY',
  new_today_c: 'Encontrados desde medianoche, hora de Puerto Rico',
  high_score: 'PUNTUACIÓN ALTA',
  high_score_c: 'Con {n} o más',
  needs_review: 'FALTA REVISAR',
  needs_review_c: 'Nadie los ha mirado todavía',
  waiting_sellers: 'ESPERANDO VENDEDOR',
  waiting_sellers_c: 'Contactados, sin respuesta',
  followups_due: 'SEGUIMIENTOS',
  followups_due_c: 'Para hoy o ya atrasados',
  inspections: 'INSPECCIONES',
  inspections_c: 'En etapa de inspección',
  finalists: 'FINALISTAS',
  finalists_c: 'Los que compara la pantalla de comparación',
  average_landed: 'COSTO PROMEDIO PUESTO AQUÍ',
  average_landed_c: 'Entre los abiertos que puntúan 70 o más',
  average_landed_none: 'No hay datos suficientes',
  best_current: 'MEJOR OPORTUNIDAD',
  best_current_c: '{n} puntos por cada $1,000 que cuesta traerlo',
  best_current_none: 'Todavía nada puntuado',
  source_health: 'SALUD DE LAS FUENTES',
  source_health_ok: 'Todas bien',
  source_health_bad: '{n} necesitan atención',
  source_health_c: 'De dónde salen los prospectos',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const has = (key: string): key is CopyKey => Object.prototype.hasOwnProperty.call(EN, key)

/* ────────────────────────────────────────────────────────────────────────────
 * Rendering one tile
 * ────────────────────────────────────────────────────────────────────────── */

const ICONS: Record<string, IconName> = {
  new_today: 'plus',
  high_score: 'star',
  needs_review: 'eye',
  waiting_sellers: 'phone',
  followups_due: 'clock',
  inspections: 'scan',
  finalists: 'check',
  average_landed: 'chart',
  best_current: 'heart',
  source_health: 'globe',
}

interface Rendered {
  label: string
  value: string
  caption: string
}

/**
 * The figure and the words, in this locale.
 *
 * `tile.value` is the server's number in every branch — nothing here counts,
 * averages or re-ranks anything. The only local work is choosing the words and
 * the formatter, which is presentation and belongs on this side.
 */
function render(tile: Tile, locale: Locale): Rendered {
  const t = (key: CopyKey): string => COPY[locale][key] ?? EN[key]
  const label = has(tile.key) ? t(tile.key) : tile.label
  const captionKey = `${tile.key}_c`
  const fallbackCaption = tile.caption ?? ''

  switch (tile.key) {
    case 'high_score': {
      const threshold = typeof tile.filter?.minScore === 'number' ? tile.filter.minScore : null
      return {
        label,
        value: tile.value === null ? DASH : String(tile.value),
        caption:
          threshold === null ? fallbackCaption : t('high_score_c').replace('{n}', String(threshold)),
      }
    }
    case 'average_landed':
      return {
        label,
        /* `—` below three qualifying rows. A mean over two hand-entered
           listings is noise wearing the authority of a statistic, and the
           server already refused to publish one. */
        value: tile.value === null ? DASH : money(tile.value, locale),
        caption: tile.value === null ? t('average_landed_none') : t('average_landed_c'),
      }
    case 'best_current':
      return {
        label,
        /* A ref and a score. Locale-neutral, and the server's own string. */
        value: tile.value === null ? DASH : tile.display,
        caption:
          tile.value === null
            ? t('best_current_none')
            : t('best_current_c').replace('{n}', String(tile.value)),
      }
    case 'source_health':
      return {
        label,
        value: tile.value === null || tile.value === 0 ? t('source_health_ok') : String(tile.value),
        caption:
          tile.value === null || tile.value === 0
            ? t('source_health_c')
            : t('source_health_bad').replace('{n}', String(tile.value)),
      }
    default:
      return {
        label,
        value: tile.value === null ? tile.display : String(tile.value),
        caption: has(captionKey) ? t(captionKey) : fallbackCaption,
      }
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * The rail
 * ────────────────────────────────────────────────────────────────────────── */

export interface DashboardProps {
  /** `rail` is the 96px scroll-snapping strip; `column` is the ≥900px side rail. */
  orientation?: 'rail' | 'column'
  /** The feed's current query string, so the tile it came from reads as chosen. */
  activeHref?: string | null
  /** Bumping this refetches — the feed pulls to refresh and the tiles follow. */
  refreshKey?: number
}

export default function Dashboard({ orientation = 'rail', activeHref, refreshKey = 0 }: DashboardProps) {
  const locale = useLocale()
  const t = (key: CopyKey): string => COPY[locale][key] ?? EN[key]

  const [view, setView] = useState<DashboardView | null>(null)
  const [failed, setFailed] = useState(false)
  /* A ref, not the state value: reading `view` inside the effect would put it
     in the dependency array, and a fetch that depends on its own result
     refetches forever. */
  const everLoaded = useRef(false)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const body = await get<{ dashboard: DashboardView }>('/api/dashboard')
        if (!live) return
        everLoaded.current = true
        setView(body.dashboard)
        setFailed(false)
      } catch {
        /* Offline, or the sweep has never run. Keep whatever is on screen — a
           rail of zeroes reads as "nothing is happening", which is a lie. */
        if (live && !everLoaded.current) setFailed(true)
      }
    })()
    return () => {
      live = false
    }
  }, [refreshKey])

  if (failed && !view) {
    return (
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' }}>
        <Icon name="info" size={14} /> {t('failed')}
      </p>
    )
  }

  if (!view) return null

  const column = orientation === 'column'

  return (
    <nav
      aria-label={t('heading')}
      style={
        column
          ? { display: 'flex', flexDirection: 'column', gap: 8 }
          : {
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              scrollSnapType: 'x proximity',
              /* Bleed to the edges of `.app-main`'s padding so the rail reads
                 as a strip that continues off-screen, which is what tells a
                 thumb there is more to the right. */
              margin: '0 -16px 14px',
              padding: '2px 16px 8px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }
      }
    >
      {view.tiles.map((tile) => {
        const shown = render(tile, locale)
        const on = !!tile.href && !!activeHref && tile.href === activeHref
        return (
          <TileBox
            key={tile.key}
            tile={tile}
            shown={shown}
            column={column}
            active={on}
            icon={ICONS[tile.key]}
          />
        )
      })}
    </nav>
  )
}

function TileBox({
  tile,
  shown,
  column,
  active,
  icon,
}: {
  tile: Tile
  shown: Rendered
  column: boolean
  active: boolean
  icon: IconName | undefined
}) {
  const box = {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between',
    flex: column ? '0 0 auto' : '0 0 auto',
    width: column ? '100%' : 152,
    minHeight: 96,
    scrollSnapAlign: 'start' as const,
    padding: '9px 11px 10px',
    borderRadius: 12,
    border: `1px solid ${active ? 'var(--brand)' : 'var(--line)'}`,
    background: active ? 'var(--brand-soft)' : 'var(--surface)',
    color: 'var(--ink)',
    textDecoration: 'none',
    /* Dimmed, not hidden: the absence of an answer and the absence of a tile
       must not look the same. */
    opacity: tile.dimmed ? 0.55 : 1,
  }

  const inner = (
    <>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 9.5,
          fontWeight: 800,
          letterSpacing: '0.07em',
          color: active ? 'var(--brand-strong)' : 'var(--muted)',
          lineHeight: 1.2,
        }}
      >
        {icon && <Icon name={icon} size={12} />}
        {shown.label}
      </span>

      <b
        style={{
          fontSize: shown.value.length > 7 ? 19 : 27,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1.05,
          margin: '6px 0 3px',
          wordBreak: 'break-word',
        }}
      >
        {shown.value}
      </b>

      <span
        style={{
          fontSize: 10.5,
          lineHeight: 1.3,
          color: 'var(--muted)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {shown.caption}
      </span>
    </>
  )

  /* A tile with no href links nowhere ON PURPOSE — AVERAGE LANDED COST below
     three rows has no list worth opening — so it is a `div`, not a dead link. */
  if (!tile.href) return <div style={box}>{inner}</div>

  return (
    <Link to={tile.href} style={box} aria-current={active ? 'page' : undefined}>
      {inner}
    </Link>
  )
}
